import DescriptionList from 'ant-design-pro/lib/DescriptionList';
import Description from 'ant-design-pro/lib/DescriptionList/Description';
import PageHeader from 'ant-design-pro/lib/PageHeader';
import { Breadcrumb, Collapse, Empty, Table } from 'antd';
import gql from 'graphql-tag';
import React, { Component } from 'react';
import { Query } from 'react-apollo';
import { Link } from 'react-router-dom';

import { Panel } from '../../components/Panel';
import { FormatDate } from '../../components/table/Date';
import { SpeechTypeText } from '../../types/speech';
import { LessonType, Statistics, UserData } from './Enrollment';

const learnReportQuery = gql`
  query getLearnReportQuery($ids: [ID], $enrollmentIds: [Int], $serialId: String) {
    users(ids: $ids) {
      id
      name
      enrollments(ids: $enrollmentIds) {
        id
        skuId
        sku {
          id
          name
        }
        learnOverview {
          learnDayCount
          learnDuration
          knowledgePointCount
        }
        lessonProgresses(serialId: $serialId) {
          serialId
          unlockAt
          lesson {
            id
            name
            type
          }
          knowledgePoints {
            name
            highestAccuracy
            speech {
              content
              type
            }
          }
          learnReports {
            learnAt
            learnEndAt
            learnDuration
            overviewStatistics {
              accuracy
              speed
              reinforcement
            }
            knowledgePoints {
              name
              parts
              accuracy
              speed
              reinforcement
            }
            cognitiveProcesses {
              name
              parts
              accuracy
              speed
              reinforcement
            }
            applyAbilities {
              name
              parts
              accuracy
              speed
              reinforcement
            }
          }
        }
      }
    }
  }
`;

interface Props {
  match: any;
}

interface State {}

export class LessonProgressDetail extends Component<Props, State> {
  render() {
    const { id, enrollmentId, serialId } = this.props.match.params;
    return (
      <Query<UserData> query={learnReportQuery} variables={{ serialId, ids: [id], enrollmentIds: [enrollmentId] }}>
        {({ loading, error, data }) => {
          if (error) return <div>{error.message}</div>;
          if (loading) return <div>loading...</div>;
          if (!data) {
            // @ts-ignore
            return <Empty />;
          }
          const [user] = (data && data.users) || [null];

          const [enrollment] = user && user.enrollments;
          const [lessonProgress] = enrollment && enrollment.lessonProgresses;

          // 获取每次最高正确率
          const highestKnowledgePoints: Statistics[] = [];
          for (const learnReport of lessonProgress.learnReports) {
            highestKnowledgePoints.push(...learnReport.knowledgePoints);
          }

          const highAccuracyKnowledgePointsMap = new Map<string, { accuracy: number; name: string }>();
          for (const highestKnowledgePoint of highestKnowledgePoints) {
            const currentMap = highAccuracyKnowledgePointsMap.get(highestKnowledgePoint.name);
            if ((currentMap && highestKnowledgePoint.accuracy > currentMap.accuracy) || !currentMap) {
              highAccuracyKnowledgePointsMap.set(highestKnowledgePoint.name, {
                accuracy: highestKnowledgePoint.accuracy,
                name: highestKnowledgePoint.name,
              });
            }
          }

          // 每次学习的最高正确率排序
          const sortedKnowledgePoints = [...highAccuracyKnowledgePointsMap.values()]
            .sort((a, b) => b.accuracy - a.accuracy)
            .filter(item => item.accuracy);

          // 最高正确率为每次最高正确率中 最高
          const [highestAccuracy] = sortedKnowledgePoints;
          // 最低正确率为每次最高正确率中 最低
          const lowestAccuracy = sortedKnowledgePoints[sortedKnowledgePoints.length - 1];

          const description = (
            <DescriptionList size="small" title="课时预览">
              <Description term="课时">{lessonProgress.serialId}</Description>
              <Description term="解锁日期">{<FormatDate mode="date" date={lessonProgress.unlockAt} />}</Description>
              <Description term="课程类型">
                {lessonProgress.lesson.type === LessonType.LESSON ? '课时' : '练习'}
              </Description>
              <Description term="次数">{lessonProgress.learnReports.length}</Description>
              <Description term="最高正确率知识点">{highestAccuracy && highestAccuracy.name}</Description>
              <Description term="最高正确率">
                {(highestAccuracy && highestAccuracy.accuracy && `${(highestAccuracy.accuracy * 100).toFixed(0)}%`) ||
                  ''}
              </Description>
              <Description term="最低正确率知识点">{lowestAccuracy && lowestAccuracy.name}</Description>
              <Description term="最低正确率">
                {(lowestAccuracy && lowestAccuracy.accuracy && `${(lowestAccuracy.accuracy * 100).toFixed(0)}%`) || ''}
              </Description>
            </DescriptionList>
          );

          return (
            <>
              <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
                <Breadcrumb.Item>
                  <Link to={`/Home`}>首页</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <Link to={`/user/${user.id}`}>用户详情</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>课后测评结果</Breadcrumb.Item>
              </Breadcrumb>
              <PageHeader
                title={lessonProgress && lessonProgress.lesson.name}
                content={description}
                // tabList={tabList}
              />
              <Panel>
                <Collapse bordered={false} defaultActiveKey={['speech']}>
                  <Collapse.Panel header={`话术`} key="speech">
                    <Table dataSource={lessonProgress.knowledgePoints} pagination={false}>
                      <Table.Column width={150} title="知识点" dataIndex="name" key="speechKnowledgeName" />
                      <Table.Column<Statistics>
                        sorter={(a, b) => {
                          if (a.highestAccuracy === 0) return 1; // Return 1 so that b goes first
                          if (b.highestAccuracy === 0) return -1; // Return -1 so that a goes first
                          return a.highestAccuracy - b.highestAccuracy;
                        }}
                        width={75}
                        title="正确率"
                        dataIndex="highestAccuracy"
                        key="speechHighestAccuracy"
                        render={highestAccuracy => `${(highestAccuracy * 100).toFixed(0)}%`}
                      />
                      <Table.Column
                        width={70}
                        title="高/低"
                        dataIndex="speech.type"
                        key="speechType"
                        render={type => SpeechTypeText[type]}
                      />
                      <Table.Column
                        title="内容"
                        dataIndex="speech.content"
                        key="speechContent"
                        render={item => item || '-'}
                      />
                    </Table>
                  </Collapse.Panel>
                </Collapse>
                <Collapse bordered={false}>
                  {lessonProgress.learnReports.map((item, index) => (
                    <Collapse.Panel header={`第${index + 1}次学习`} key={String(index)}>
                      <Table<{
                        count: number;
                        learnAt: string;
                        learnEndAt: string;
                        learnDuration: number;
                        reinforcement: number;
                        accuracy: string;
                        speed: string;
                        knowledgePoints: Statistics[];
                      }>
                        dataSource={[
                          {
                            count: index + 1,
                            learnAt: item.learnAt,
                            learnEndAt: item.learnEndAt,
                            learnDuration: item.learnDuration,
                            reinforcement: item.overviewStatistics.reinforcement,
                            accuracy:
                              (item.overviewStatistics.accuracy && item.overviewStatistics.accuracy.toFixed(2)) || '',
                            speed: (item.overviewStatistics.accuracy && item.overviewStatistics.speed.toFixed(2)) || '',
                            knowledgePoints: item.knowledgePoints,
                          },
                        ]}
                        pagination={false}
                        rowKey={item => `${item}`}
                        loading={loading}
                      >
                        <Table.Column title="学习次数" dataIndex="count" key="count" />
                        <Table.Column
                          title="学习日期"
                          dataIndex="learnAt"
                          key="learnAt"
                          render={learnAt => <FormatDate mode="datetime" date={learnAt} />}
                        />
                        <Table.Column
                          title="学完日期"
                          dataIndex="learnEndAt"
                          key="learnEndAt"
                          render={learnEndAt => <FormatDate mode="datetime" date={learnEndAt} />}
                        />
                        <Table.Column title="学习时长" dataIndex="learnDuration" key="learnDuration" />
                        <Table.Column title="答题速度" dataIndex="speed" key="speed" />
                        <Table.Column title="正确率" dataIndex="accuracy" key="accuracy" />
                        <Table.Column
                          title="最低正确率"
                          dataIndex="knowledgePoints"
                          key="knowledgePoints"
                          render={knowledgePoints => {
                            if (!knowledgePoints.length) {
                              return '无';
                            }
                            const [lowestAccuracy] = knowledgePoints.sort(
                              (a: Statistics, b: Statistics) => a.accuracy - b.accuracy,
                            );
                            return lowestAccuracy.name;
                          }}
                        />
                        <Table.Column
                          title="最高正确率"
                          dataIndex="knowledgePoints"
                          key="knowledgePoints"
                          render={knowledgePoints => {
                            if (!knowledgePoints.length) {
                              return '无';
                            }
                            const [highestAccuracy] = knowledgePoints.sort(
                              (a: Statistics, b: Statistics) => b.accuracy - a.accuracy,
                            );
                            return highestAccuracy.name;
                          }}
                        />
                      </Table>

                      <Table loading={loading} dataSource={item.knowledgePoints} pagination={false}>
                        <Table.Column title="知识点" dataIndex="name" key="name" width={150} />
                        <Table.Column title="强化次数" dataIndex="reinforcement" key="reinforcement" width={150} />
                        <Table.Column
                          title="正确率"
                          dataIndex="accuracy"
                          key="accuracy"
                          width={150}
                          render={accuracy => (accuracy && `${(accuracy * 100).toFixed(0)}%`) || ''}
                        />
                        <Table.Column title="答题速度" dataIndex="speed" key="speed" width={150} />
                      </Table>
                      <Table loading={loading} dataSource={item.cognitiveProcesses} pagination={false}>
                        <Table.Column title="认知过程" dataIndex="name" key="name" width={150} />
                        <Table.Column title="强化次数" dataIndex="reinforcement" key="reinforcement" width={150} />
                        <Table.Column
                          title="正确率"
                          dataIndex="accuracy"
                          key="accuracy"
                          width={150}
                          render={accuracy => (accuracy && `${(accuracy * 100).toFixed(0)}%`) || ''}
                        />
                        <Table.Column title="答题速度" dataIndex="speed" key="speed" width={150} />
                      </Table>
                      <Table loading={loading} dataSource={item.applyAbilities} pagination={false}>
                        <Table.Column title="应用能力" dataIndex="name" key="name" width={150} />
                        <Table.Column title="强化次数" dataIndex="reinforcement" key="reinforcement" width={150} />
                        <Table.Column
                          title="正确率"
                          dataIndex="accuracy"
                          key="accuracy"
                          width={150}
                          render={accuracy => (accuracy && `${(accuracy * 100).toFixed(0)}%`) || ''}
                        />
                        <Table.Column title="答题速度" dataIndex="speed" key="speed" width={150} />
                      </Table>
                    </Collapse.Panel>
                  ))}
                </Collapse>
              </Panel>
            </>
          );
        }}
      </Query>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/enrollment/LessonProgress.tsx